// maxLength: optional character limit for truncation.
//   omitted / null → 152 chars (default, suitable for meta description tags)
//   500            → Google's schema.org description limit
export function truncateDescription(description, maxLength) {
    const limit = maxLength != null ? maxLength : 152;

    // Strip all HTML tags, then collapse whitespace
    description = description.replace(/<[^>]*>/g, ' ');
    description = description.replace(/\s+/g, ' ').trim();

    if (description.length <= limit) {
      return description;
    }

    const truncated = description.substring(0, limit);
    const lastSpaceIndex = truncated.lastIndexOf(' ');

    if (lastSpaceIndex === -1) {
      return truncated + '...';
    }

    return truncated.substring(0, lastSpaceIndex) + '...';
  };

export const TITLE_MAX_LENGTH = 60;
export const TITLE_BRAND_SUFFIX = '| Summit';

// Google's documented maximum for the schema.org `description` property.
export const SCHEMA_DESCRIPTION_MAX = 500;

const TITLE_SANITY_MAX = 150;

/*
 * Build a title tag.
 *
 * Fits by shedding boilerplate, never by cutting the name. See DESIGN NOTE.
 *
 * options:
 *   override   - an explicit hand-written title; used verbatim if present
 *   qualifier  - e.g. 'CE Course'; dropped first when space is tight
 *   suffix     - brand; dropped second
 *   maxLength  - target budget
 */
export function buildSeoTitle(name, options) {
  const opts = options || {};
  const qualifier = opts.qualifier || '';
  const suffix = opts.suffix !== undefined ? opts.suffix : TITLE_BRAND_SUFFIX;
  const maxLength = opts.maxLength || TITLE_MAX_LENGTH;

  const clean = (value) => String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // A hand-written title wins outright - a human decided what matters.
  const override = clean(opts.override);
  if (override) {
    return override;
  }

  const cleanName = clean(name);

  // No usable name yet (data still loading) - brand alone, never a dangling
  // qualifier like "CE Course | Summit".
  if (!cleanName) {
    return 'Summit Professional Education';
  }

  // Most informative variant first; fall back only when it does not fit.
  const variants = [
    [qualifier, suffix],
    [suffix],
    [qualifier],
    [],
  ];

  for (const parts of variants) {
    const tail = parts.filter(Boolean).join(' ').trim();
    const candidate = tail ? `${cleanName} ${tail}` : cleanName;
    if (candidate.length <= maxLength) {
      return candidate;
    }
  }

  // Name alone exceeds the budget. Emit it whole: a long title is displayed
  // truncated by Google, which is strictly better than colliding with another
  // page. These are the pages that want a hand-written `override`.
  return cleanName.length > TITLE_SANITY_MAX
    ? cleanName.substring(0, TITLE_SANITY_MAX).trim()
    : cleanName;
}